import {
    faCirclePlay,
    faClipboard,
    faCopy,
    faPaperPlane
} from '@fortawesome/free-regular-svg-icons';
import {
    faBars,
    faCalendar,
    faClockRotateLeft,
    faDownload,
    faEdit,
    faFilePen,
    faPeopleGroup,
    faPeopleLine,
    faPeopleRoof,
    faPersonRunning,
    faTrashCan
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAssessmentsQuery } from 'hooks/queries/useAssessmentsQuery';
import { usePracticesQuery } from 'hooks/queries/usePracticesQuery';
import { useTeamsQuery } from 'hooks/queries/useTeamsQuery';
import { useUserFunctionsQuery } from 'hooks/queries/useUserFunctionsQuery';
import { useUsersQuery } from 'hooks/queries/useUsersQuery';
import { DatePicker, Flex, Line } from 'components';
import { NotesModal } from 'components/NotesModal/NotesModal';
import { ExecutionProgressTracking } from 'components/ExecutionProgressTracking/ExecutionProgressTracking';
import { TasksConnectedWithAssessmentModal } from 'components/TasksConnectedWithAssessmentModal/TasksConnectedWithAssessmentModal';
import { H2, H4, P } from 'components/Typography/Typography';
import {
    Button,
    FieldLabel,
    Select,
    UserSelect,
    SwitchButton,
    TextField,
    TextareaField
} from 'components/_form';
import { DescriptionModal } from 'components/_form/DescriptionModal/DescriptionModal';
import {
    NestedSelect,
    IOption as OptionForSelect
} from 'components/_form/NestedSelect/NestedSelect';
import { findOptionByValueInTree } from 'components/_form/NestedSelect/utilities';
import { PadlockButton } from 'components/_form/PadlockButton/PadlockButton';
import { IOption } from 'components/_form/Select/Select';
import { LabelPosition } from 'components/_form/TextField/TextField';
import { useAssessmentContext } from 'contexts/AssessmentContext';
import { format } from 'date-fns';
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { theme } from 'styles';
import { IDetailedAssessment } from 'types/assessments';
import { Methodologies } from 'types/methologies';
import {
    parentToChildrenAreasStructure,
    parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import { mapUsersToSelectOptions } from 'utilities/userSelectOption';
import {
  filterUserFunctionsByAreaIds,
  mapUserFunctionsToOptions
} from 'utilities/userFunctions';
import { getTeamMembers } from 'utilities/teamMembers';
import {
    makeMethodologyPracticeTree,
    parseMethodologyPracticeTreeForSelect
} from 'utilities/practices';
import { hourChecker, minuteChecker } from 'utilities/timePicker';
import { IconButton } from '../../../IconButton/IconButton';
import { FormDataControl } from '../CreateAssessmentModal';
import { CriteriaListModal } from '../CriteriaListModal/CriteriaListModal';
import { DeleteAssessmentModal } from '../DeleteAssessmentModal/DeleteAssessmentModal';
import { AssessmentParticipantsModal } from '../AssessmentParticipantsModal/AssessmentParticipantsModal';
import { AssessmentStatusModal } from '../AssessmentStatusModal/AssessmentStatusModal';
import {
    IconButtonContainer,
    MainSection,
    TitleContainer
} from './FormAssessment.styled';

interface Props {
  selectedPractice: OptionForSelect[];
  setSelectedPractice: React.Dispatch<React.SetStateAction<OptionForSelect[]>>;
  canChangeAssessmentStatus?: boolean;
  selectedAssessment?: IDetailedAssessment;
  setSelectedAssessment?: Dispatch<
    SetStateAction<IDetailedAssessment | undefined>
  >;
  handleSubmitAssessmentForm: (
    data: FormDataControl,
    closeOnSubmit?: boolean
  ) => Promise<IDetailedAssessment | undefined>;
  handleDeleteAssessment?: () => {};
}

export interface ITeamOption extends IOption {
  members: ReturnType<typeof getTeamMembers>;
}

export const FormAssessment: React.FC<Props> = ({
  selectedPractice,
  setSelectedPractice,
  canChangeAssessmentStatus = true,
  selectedAssessment,
  setSelectedAssessment,
  handleSubmitAssessmentForm,
  handleDeleteAssessment
}) => {
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [methodologyTree, setMethodologyTree] = useState<Methodologies>({});
  const [methodologyTreeForSelect, setMethodologyTreeForSelect] = useState<
    OptionForSelect[]
  >([]);
  const [isAssessmentStatusModalOpen, setIsAssessmentStatusModalOpen] =
    useState(false);
  const [isCriteriaListModalOpen, setIsCriteriaListModalOpen] = useState(false);
  const [isTasksModalOpen, setIsTasksModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [assessmentsToRelation, setAssessmentsToRelation] = useState<IOption[]>(
    []
  );
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [areas, setAreas] = useState<OptionForSelect[]>([]);
  const { data: users = [] } = useUsersQuery();
  const usersList = useMemo(
    () => mapUsersToSelectOptions(users),
    [users]
  );
  const [teamsList, setTeamsList] = useState<ITeamOption[]>([]);
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  const [userFunctionsList, setUserFunctionsList] = useState<IOption[]>([]);

  const { t } = useTranslation();
  const ctx = useAssessmentContext();

  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext<FormDataControl>();

  const areaId = watch('area_id');
  const { data: assessmentsData = [] } = useAssessmentsQuery({
    filters: { area_ids: areaId ? [Number(areaId)] : [] }
  });
  const { data: practicesData = [] } = usePracticesQuery();
  const { data: teamsData = [] } = useTeamsQuery();
  const { data: userFunctionsData = [] } = useUserFunctionsQuery();

  const assessmentTypes: IOption[] = [
    {
      label: t('assessmentsView.assessmentTypes.target') as string,
      value: 'target'
    },
    {
      label: t('assessmentsView.assessmentTypes.baseline') as string,
      value: 'baseline'
    },
    {
      label: t('assessmentsView.assessmentTypes.current') as string,
      value: 'current'
    },
    {
      label: t('assessmentsView.assessmentTypes.derivative') as string,
      value: 'derivative'
    },
    {
      label: t('assessmentsView.assessmentTypes.normal') as string,
      value: 'normal'
    }
  ];

  const handleGetAreas = useCallback(async () => {
    const areasData = ctx.areas;

    setAreas(
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      )
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ctx.areas)]);

  useEffect(() => {
    const methodologyObject = makeMethodologyPracticeTree(practicesData);
    setMethodologyTree(methodologyObject);
    setMethodologyTreeForSelect(
      parseMethodologyPracticeTreeForSelect(methodologyObject)
    );
  }, [practicesData]);

  const handleSelectedAreaIdChange = useCallback(() => {
    const selectedAreaIds = ctx.filters.area_ids.value;
    if (!selectedAreaIds.length || !areas.length) return;

    const nextAreaId =
      selectedAreaIds
        .map((id) => String(id))
        .find((id) => findOptionByValueInTree(areas, id) != null) ?? '';

    if (areaId !== nextAreaId) {
      setValue('area_id', nextAreaId);
    }
  }, [ctx.filters.area_ids.value, areas, setValue, areaId]);

  useEffect(() => {
    setAssessmentsToRelation(
      assessmentsData.map((assessment) => ({
        label: assessment.name,
        value: assessment.id.toString()
      }))
    );
  }, [assessmentsData]);

  useEffect(() => {
    setTeamsList(
      teamsData.map((team) => ({
        value: team.id?.toString(),
        label: team.name,
        members: getTeamMembers(team)
      }))
    );
  }, [teamsData]);

  useEffect(() => {
    setUserFunctionsList(
      mapUserFunctionsToOptions(
        filterUserFunctionsByAreaIds(
          userFunctionsData,
          areaId ? [String(areaId)] : []
        )
      )
    );
  }, [userFunctionsData, areaId]);

  const handleLaunchAssessment = async () => {
    const AssessmentData = await handleSubmitAssessmentForm(getValues(), false);

    if (AssessmentData) {
      ctx.setAssessmentToEditId(AssessmentData.id);
      ctx.setSelectedBlock(AssessmentData.practice?.blocks[0]);
    }
  };

  useEffect(() => {
    handleGetAreas();
  }, [handleGetAreas]);

  useEffect(() => {
    handleSelectedAreaIdChange();
  }, [handleSelectedAreaIdChange]);

  const groupOptions = [
    {
      label: (
        <Flex gap="5px">
          <FontAwesomeIcon
            icon={faPeopleRoof}
            color={theme.palette.accent.blue}
          />
          {t('assessmentsView.management')}
        </Flex>
      ),
      value: 'management'
    },
    {
      label: (
        <Flex gap="5px">
          <FontAwesomeIcon
            icon={faPeopleLine}
            color={theme.palette.accent.red}
          />
          {t('assessmentsView.operators')}
        </Flex>
      ),
      value: 'operators'
    }
  ];

  return (
    <>
      <MainSection flexDirection={['column', 'row']}>
        <Flex flexDirection="column" width={['100%', '50%']} gap="8px">
          <Flex width="95%" flexDirection="row" justifyContent="space-between">
            <Flex flexDirection="column" width="65%">
              <NestedSelect
                label={t('createTaskView.practice')}
                selectedOptions={selectedPractice}
                setSelectedOptions={setSelectedPractice}
                options={methodologyTreeForSelect}
                isOneOptionSelect={true}
              />
            </Flex>
            <Flex flexDirection="column" width="30%">
              <Controller
                control={control}
                name="version"
                render={({ field: { onChange, value } }) => (
                  <Select
                    isMulti={false}
                    label={t('createTaskView.version')}
                    selectedOptions={value}
                    options={[{ value: '3.0-GW', label: '3.0-GW' }]}
                    withoutMargin
                    onChange={(val) => {
                      onChange(val);
                    }}
                  />
                )}
              />
            </Flex>
          </Flex>
          <Flex width="100%" flexDirection="row" justifyContent="space-between">
            <Flex flexDirection="column" width="100%">
              <TextareaField
                label={t('createTaskView.assessmentName')}
                width="95%"
                {...register('name')}
              />
            </Flex>
          </Flex>
          <Flex width="100%" flexDirection="row" justifyContent="space-between">
            <Flex flexDirection="column" width="95%">
              <TextareaField
                {...register('description')}
                label={t('createTaskView.description') || ''}
                rows={5}
                error={t(errors?.description?.message || '')}
                disabled
                ExternalItem={
                  <Button
                    variant="secondary"
                    icon={
                      <FontAwesomeIcon color="grey" size="2x" icon={faEdit} />
                    }
                    onClick={() => {
                      setIsDescriptionModalOpen(
                        (prevIsDescriptionModalOpen) =>
                          !prevIsDescriptionModalOpen
                      );
                    }}
                  />
                }
              />
            </Flex>
          </Flex>
          <Flex width="95%" flexDirection="row" justifyContent="space-between">
            <Flex flexDirection="column" width="30%">
              <Controller
                control={control}
                name="group"
                render={({ field: { onChange, value } }) => (
                  <Select
                    label={t('createTaskView.assessmentOnLevel')}
                    withoutMargin
                    selectedOptions={groupOptions.find(
                      (groupOption) => groupOption.value === value
                    )}
                    onChange={(option: IOption) => {
                      onChange(option.value);
                    }}
                    options={groupOptions}
                  />
                )}
              />
            </Flex>

            <Flex flexDirection="column" width="65%">
              <Controller
                control={control}
                name="related_to_assessment_id"
                render={({ field: { onChange, value } }) => (
                  <Select
                    label={t('createTaskView.associateWith')}
                    withoutMargin
                    selectedOptions={assessmentsToRelation.find(
                      (assessmentToRelation) =>
                        assessmentToRelation.value === value
                    )}
                    onChange={(option: IOption) => {
                      onChange(option.value);
                    }}
                    options={assessmentsToRelation}
                  />
                )}
              />
            </Flex>
          </Flex>

          <Flex
            width="95%"
            flexDirection="row"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Flex flexDirection="column" width="30%">
              <FieldLabel htmlFor="priority">
                {t('createTaskView.assessmentType')}
              </FieldLabel>
              <Controller
                control={control}
                name="assessment_type"
                render={({ field: { onChange, value } }) => (
                  <Select
                    isMulti={false}
                    selectedOptions={(() => {
                      let selectedAssessment: IOption = {
                        label: '',
                        value: ''
                      };

                      assessmentTypes.forEach((assessmentType) => {
                        if (assessmentType.value === value.value)
                          selectedAssessment = assessmentType;
                      });

                      return selectedAssessment;
                    })()}
                    options={assessmentTypes}
                    onChange={(val) => {
                      onChange(val);
                    }}
                  />
                )}
              />
            </Flex>
            <Flex
              flexDirection="column"
              width="auto"
              justifyContent="space-evenly"
            >
              <TitleContainer variant="body" lineHeight="21px">
                {t('createTaskView.assessmentScore')}:
              </TitleContainer>
              <TitleContainer
                variant="body"
                lineHeight="21px"
                textAlign="center"
              >
                0.00
              </TitleContainer>
            </Flex>
            <Flex
              flexDirection="column"
              width="auto"
              justifyContent="space-evenly"
            >
              <TitleContainer variant="body" lineHeight="21px">
                {t('createTaskView.isFinished')}?
              </TitleContainer>
              <Flex flexDirection="row" justifyContent="center">
                <SwitchButton value={isFinished} onChange={setIsFinished} />
              </Flex>
            </Flex>
            <Flex
              flexDirection="column"
              width="auto"
              justifyContent="space-evenly"
            >
              <TitleContainer variant="body" lineHeight="21px">
                {t('createTaskView.isClosed')}?
              </TitleContainer>
              <PadlockButton
                locked={
                  !!selectedAssessment?.assessments_closings.find(
                    ({ closed_at }) => closed_at
                  )
                }
                onClick={() => {
                  if (canChangeAssessmentStatus === true) {
                    setIsAssessmentStatusModalOpen(true);
                  }
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex flexDirection="column" width={['100%', '50%']}>
          <Flex
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="end"
            gap="10px"
          >
            <Flex flexDirection="column" width="100%">
              <FieldLabel htmlFor="area-select">
                {t('createTaskView.areas')}
              </FieldLabel>

              <Controller
                control={control}
                name="area_id"
                render={({ field: { onChange, value } }) => (
                  <NestedSelect
                    selectedOptions={
                      value
                        ? ([findOptionByValueInTree(areas, value)].filter(
                            (o): o is OptionForSelect => !!o
                          ) as OptionForSelect[])
                        : []
                    }
                    setSelectedOptions={(newOptions) => {
                      onChange(newOptions[0]?.value ?? '');
                    }}
                    options={areas}
                    isOneOptionSelect
                    singleSelectAnyNode
                  />
                )}
              />
            </Flex>
            <Button variant="grey">
              <FontAwesomeIcon icon={faCopy} />
              <P variant="body">{t('createTaskView.copy')}</P>
            </Button>
          </Flex>
          <Flex
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
            marginTop="10px"
          >
            <Flex flexDirection="column" width="100%">
              <Flex width="95%">
                <Controller
                  control={control}
                  name="leader_id"
                  render={({ field: { onChange, value } }) => (
                    <UserSelect
                      label={t('createAssessmentView.leader')}
                      selectedOptions={usersList.find(
                        (userOption) => userOption.value === String(value)
                      )}
                      onChange={(val: IOption) => {
                        onChange(val.value);
                      }}
                      options={usersList}
                    />
                  )}
                />
              </Flex>
            </Flex>
            <Flex flexDirection="column" width="100%">
              <TextField
                label={t('createTaskView.author')}
                value={
                  usersList
                    .find(({ value }) => value === watch('author_id'))
                    ?.label?.toString() || ''
                }
                disabled
              />
            </Flex>
          </Flex>
          <Flex flexDirection="column" width="100%">
            <Controller
              control={control}
              name="team_ids"
              render={({ field: { onChange, value } }) => (
                <Select
                  isMulti
                  label={t('createTaskView.auditTeam')}
                  selectedOptions={teamsList.filter((teamItem) =>
                    value?.includes(teamItem.value)
                  )}
                  onChange={(options: IOption[]) => {
                    onChange(options.length ? [options[0].value] : []);
                  }}
                  options={teamsList}
                />
              )}
            />
          </Flex>
          <Flex
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
            marginTop="10px"
          >
            <Flex flexDirection="column" width="100%">
              <TitleContainer variant="body">
                {t('createTaskView.participants')}
              </TitleContainer>
              <Flex flexDirection="row" justifyContent="space-between">
                <TextareaField
                  disabled
                  width="80%"
                  rows={2}
                  value={watch('assessment_participations_attributes')?.map(
                    (taskParticipationAttribute) =>
                      `${taskParticipationAttribute.label} \n`
                  )}
                />
                <IconButtonContainer
                  flexDirection="column"
                  width="100px"
                  height="55px"
                  onClick={() => {
                    setIsParticipantsModalOpen(
                      (prevIsParticipantsModalOpen) =>
                        !prevIsParticipantsModalOpen
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faPeopleGroup} size="xl" />
                  <H2 variant="h3">{t('createTaskView.manage')}</H2>
                </IconButtonContainer>
              </Flex>
            </Flex>
          </Flex>

          <Flex
            width="90%"
            flexDirection="row"
            justifyContent="space-between"
            marginTop="22px"
            flexWrap="wrap"
            gap="5px"
          >
            <IconButtonContainer
              flexDirection="row"
              width="120px"
              height="45px"
              onClick={handleLaunchAssessment}
            >
              <FontAwesomeIcon icon={faCirclePlay} size="xl" />
              <H2 variant="h3">{t('createTaskView.launch')}</H2>
            </IconButtonContainer>
            <Flex justifyContent="center" gap="4px" flexWrap="wrap">
              <IconButton
                onClick={() => {
                  setIsTasksModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faBars} size="2x" />
              </IconButton>
              <IconButton onClick={() => setIsCriteriaListModalOpen(true)}>
                <FontAwesomeIcon icon={faFilePen} size="2x" />
              </IconButton>
              <IconButton>
                <FontAwesomeIcon icon={faDownload} size="2x" />
              </IconButton>
              <IconButton
                disabled={!selectedAssessment}
                onClick={() => {
                  setIsNotesModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faClipboard} size="2x" />
              </IconButton>
              <IconButton>
                <FontAwesomeIcon icon={faPaperPlane} size="2x" />
              </IconButton>
              <IconButton>
                <FontAwesomeIcon icon={faClockRotateLeft} size="2x" />
              </IconButton>
              <IconButton
                onClick={() => {
                  setIsDeleteModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faTrashCan} size="2x" />
              </IconButton>
            </Flex>
          </Flex>
          <Flex
            width="90%"
            flexDirection="row"
            justifyContent="center"
            marginTop="10px"
          >
            <Flex flexDirection="column">
              <TitleContainer variant="body" lineHeight="21px">
                {t('createTaskView.show')}:
              </TitleContainer>
              <IconButtonContainer
                flexDirection="row"
                width="100px"
                height="30px"
                bgColor={theme.palette.primary.main}
                onClick={() => {
                  setIsTasksModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faPersonRunning} />
                <H2 variant="h3">{t('createTaskView.tasks')}</H2>
              </IconButtonContainer>
            </Flex>
          </Flex>
        </Flex>
      </MainSection>

      <Flex flexDirection="column">
        <Flex flexDirection="column">
          <H4 variant="h4" color="coloured">
            {t('tasks.planning')}
          </H4>
          <Line />

          <Flex alignItems="center" flexDirection={['column', 'row']}>
            <Flex mb={3} mr={2} width={['100%', '25%']}>
              <Controller
                control={control}
                name="planned_start_date"
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label={t('tasks.plannedStartDate') || ''}
                    icon={<FontAwesomeIcon color="grey" icon={faCalendar} />}
                    value={value ? new Date(value) : null}
                    onChange={(newValue) => {
                      onChange(newValue && format(newValue, 'yyyy-MM-dd'));
                    }}
                  />
                )}
              />
            </Flex>

            <Flex mb={3} mr={2} width={['100%', '25%']}>
              <Controller
                control={control}
                name="planned_completion_date"
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label={t('tasks.timeLimitForCompletion') || ''}
                    icon={<FontAwesomeIcon color="grey" icon={faCalendar} />}
                    value={value ? new Date(value) : null}
                    onChange={(newValue) => {
                      onChange(newValue && format(newValue, 'yyyy-MM-dd'));
                    }}
                  />
                )}
              />
            </Flex>
            <Flex flexDirection="column" width={['100%', '30%']} mt="4px">
              <FieldLabel>{t('tasks.plannedTime')}</FieldLabel>
              <Flex mr={1} width="100%" alignItems="end">
                <Flex mr={1} width="50%">
                  <Controller
                    control={control}
                    name="planned_duration"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label="tasks.hrs."
                        type="number"
                        value={Number(
                          value?.slice(
                            value.indexOf('T') + 1,
                            value.indexOf('H')
                          )
                        )}
                        labelPosition={LabelPosition.BOTTOM}
                        onChange={(newValue) => {
                          if (!hourChecker(newValue.target.value)) return;

                          onChange(
                            `${value.slice(0, value.indexOf('T') + 1)}${
                              newValue.target.value
                            }${value.slice(value.indexOf('H'))}`
                          );
                        }}
                      />
                    )}
                  />
                </Flex>
                <Flex width="50%">
                  <Controller
                    control={control}
                    name="planned_duration"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label="tasks.min."
                        type="number"
                        value={Number(
                          value?.slice(
                            value.indexOf('H') + 1,
                            value.indexOf('M')
                          )
                        )}
                        labelPosition={LabelPosition.BOTTOM}
                        onChange={(newValue) => {
                          if (!minuteChecker(newValue.target.value)) return;

                          onChange(
                            `${value.slice(0, value.indexOf('H') + 1)}${
                              newValue.target.value
                            }${value.slice(value.indexOf('M'))}`
                          );
                        }}
                      />
                    )}
                  />
                </Flex>
              </Flex>
            </Flex>
            <Flex width={['100%', '30%']} mt={3} justifyContent="center">
              <Controller
                control={control}
                name="only_realization"
                render={({ field: { onChange, value } }) => (
                  <SwitchButton
                    label="tasks.realizationOnly (D)"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Flex>
          </Flex>
        </Flex>

        <Flex flexDirection="column">
          <H4 variant="h4" color="green">
            {t('tasks.chartImplementation')}
          </H4>
          <Line variant="tertiary" />

          <Flex alignItems="center" flexDirection={['column', 'row']}>
            <Flex mb={3} mr={2} width={['100%', '25%']}>
              <Controller
                control={control}
                name="start_time"
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label={t('tasks.plannedStartDate') || ''}
                    icon={<FontAwesomeIcon color="grey" icon={faCalendar} />}
                    value={value ? new Date(value) : null}
                    onChange={(newValue) => {
                      onChange(newValue && format(newValue, 'yyyy-MM-dd'));
                    }}
                  />
                )}
              />
            </Flex>

            <Flex mb={3} mr={2} width={['100%', '25%']}>
              <Controller
                control={control}
                name="completion_time"
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label={t('tasks.timeLimitForCompletion') || ''}
                    icon={<FontAwesomeIcon color="grey" icon={faCalendar} />}
                    value={value ? new Date(value) : null}
                    onChange={(newValue) => {
                      onChange(newValue && format(newValue, 'yyyy-MM-dd'));
                    }}
                  />
                )}
              />
            </Flex>
            <Flex flexDirection="column" width={['100%', '30%']} mt="6px">
              <FieldLabel>{t('tasks.plannedTime')}</FieldLabel>
              <Flex mr={1} width="100%" alignItems="end">
                <Flex mr={1} width="50%">
                  <Controller
                    control={control}
                    name="duration"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label="tasks.hrs."
                        type="number"
                        value={Number(
                          value?.slice(
                            value.indexOf('T') + 1,
                            value.indexOf('H')
                          )
                        )}
                        labelPosition={LabelPosition.BOTTOM}
                        onChange={(newValue) => {
                          if (!minuteChecker(newValue.target.value)) return;

                          onChange(
                            `${value.slice(0, value.indexOf('T') + 1)}${
                              newValue.target.value
                            }${value.slice(value.indexOf('H'))}`
                          );
                        }}
                      />
                    )}
                  />
                </Flex>
                <Flex width="50%">
                  <Controller
                    control={control}
                    name="duration"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label="tasks.min."
                        type="number"
                        value={Number(
                          value?.slice(
                            value.indexOf('H') + 1,
                            value.indexOf('M')
                          )
                        )}
                        labelPosition={LabelPosition.BOTTOM}
                        onChange={(newValue) => {
                          if (!minuteChecker(newValue.target.value)) return;

                          onChange(
                            `${value.slice(0, value.indexOf('H') + 1)}${
                              newValue.target.value
                            }${value.slice(value.indexOf('M'))}`
                          );
                        }}
                      />
                    )}
                  />
                </Flex>
              </Flex>
            </Flex>
            <Flex width={['100%', '30%']}>
              <ExecutionProgressTracking
                label={t('createTaskView.status')}
                showStatusInfo
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      <DescriptionModal
        value={watch('description')}
        setValue={(value) => {
          setValue('description', value || '');
        }}
        isOpen={isDescriptionModalOpen}
        onCancelClick={() => {
          setIsDescriptionModalOpen(
            (prevIsDescriptionModalOpen) => !prevIsDescriptionModalOpen
          );
        }}
      />

      <AssessmentParticipantsModal
        isOpen={isParticipantsModalOpen}
        control={control}
        watch={watch}
        setValue={setValue}
        usersList={usersList}
        teamsList={teamsList}
        userFunctionsList={userFunctionsList}
        onCancelClick={() => {
          setIsParticipantsModalOpen(
            (prevIsParticipantsModalOpen) => !prevIsParticipantsModalOpen
          );
        }}
        saveAndClose={() => {
          setIsParticipantsModalOpen(
            (prevIsParticipantsModalOpen) => !prevIsParticipantsModalOpen
          );
        }}
      />

      {isAssessmentStatusModalOpen && (
        <AssessmentStatusModal
          watch={watch}
          setValue={setValue}
          isOpen={isAssessmentStatusModalOpen}
          onCancelClick={() => setIsAssessmentStatusModalOpen(false)}
          usersList={usersList}
          userFunctionsList={userFunctionsList}
          selectedAssessment={selectedAssessment}
          setSelectedAssessment={setSelectedAssessment}
        />
      )}
      <CriteriaListModal
        isOpen={isCriteriaListModalOpen}
        onCancelClick={() => setIsCriteriaListModalOpen(false)}
      />

      <TasksConnectedWithAssessmentModal
        isOpen={isTasksModalOpen}
        onCancelClick={() => setIsTasksModalOpen(false)}
      />

      <NotesModal
        isOpen={isNotesModalOpen}
        onCancelClick={() => {
          setIsNotesModalOpen((prevIsNotesModalOpen) => !prevIsNotesModalOpen);
        }}
        noteableId={selectedAssessment?.id}
        noteableType="Assessment"
        notes={selectedAssessment?.notes}
        setNotes={(newNotes) =>
          setSelectedAssessment?.(
            (prevSelectedItemData) =>
              prevSelectedItemData && {
                ...prevSelectedItemData,
                notes: newNotes
              }
          )
        }
      />

      {handleDeleteAssessment && (
        <DeleteAssessmentModal
          onSubmit={handleDeleteAssessment}
          isOpen={isDeleteModalOpen}
          onCancelClick={() => {
            setIsDeleteModalOpen(false);
          }}
        />
      )}
    </>
  );
};
